import type {
  CatalogDraftWrite,
  MerchantFeedPage,
  OrderCancellationReason,
} from "@gobro/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, itemsFrom, MerchantApi } from "./api";
import { CatalogManager } from "./catalog-manager";
import type {
  DashboardSnapshot,
  Locale,
  MerchantOrder,
  Organization,
  QueuedMutation,
  Store,
  Tab,
} from "./domain";
import { dateRange, money, time } from "./format";
import { translate } from "./i18n";
import { Brand, Icon } from "./icons";
import {
  IndexedDbMerchantStorage,
  MemoryMerchantStorage,
  type MerchantStorage,
} from "./offline-store";
import { subscribeMerchantWebPush } from "./push";
import { SyncEngine } from "./sync-engine";
import "./styles.css";

type Phase = "boot" | "login" | "otp" | "select" | "dashboard";
type Connection = "live" | "offline" | "sync";

interface AppProps {
  api?: MerchantApi;
  previewData?: {
    organization: Organization;
    snapshot: DashboardSnapshot;
    store: Store;
  };
  storage?: MerchantStorage;
}

export function App({
  api = new MerchantApi(),
  previewData,
  storage = previewData ? new MemoryMerchantStorage() : new IndexedDbMerchantStorage(),
}: AppProps) {
  const isPreview = previewData !== undefined;
  const [phase, setPhase] = useState<Phase>(isPreview ? "dashboard" : "boot");
  const [locale, setLocale] = useState<Locale>("ar");
  const [tab, setTab] = useState<Tab>("orders");
  const [organizations, setOrganizations] = useState<Organization[]>(
    previewData ? [previewData.organization] : [],
  );
  const [stores, setStores] = useState<Store[]>(previewData ? [previewData.store] : []);
  const [organization, setOrganization] = useState<Organization | null>(
    previewData?.organization ?? null,
  );
  const [store, setStore] = useState<Store | null>(previewData?.store ?? null);
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(previewData?.snapshot ?? null);
  const [connection, setConnection] = useState<Connection>(navigator.onLine ? "sync" : "offline");
  const [pending, setPending] = useState<QueuedMutation[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [phone, setPhone] = useState("+216");
  const [code, setCode] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const cursorRef = useRef(previewData?.snapshot.feed.nextCursor ?? "0");
  const audioRef = useRef<AudioContext | null>(null);
  const syncEngine = useMemo(() => new SyncEngine(api, storage), [api, storage]);
  const tr = useCallback(
    (key: Parameters<typeof translate>[1]) => translate(locale, key),
    [locale],
  );

  const readMutations = useCallback(
    async () => setPending(await storage.getMutations()),
    [storage],
  );

  const discardMutation = useCallback(
    async (id: string) => {
      await storage.deleteMutation(id);
      await readMutations();
      setMessage(null);
    },
    [readMutations, storage],
  );

  const refresh = useCallback(
    async (targetOrganization: Organization, targetStore: Store) => {
      setConnection(navigator.onLine ? "sync" : "offline");
      const cached = await storage.getSnapshot(targetStore.id);
      if (!navigator.onLine) {
        if (cached) {
          setSnapshot(cached);
          cursorRef.current = cached.feed.nextCursor;
        }
        setConnection("offline");
        return;
      }
      try {
        const range = dateRange();
        const [workspace, orders, statement, feed] = await Promise.all([
          api.workspace(targetOrganization.id, targetStore.id),
          api.orders(targetOrganization.id, targetStore.id),
          api.statement(targetOrganization.id, targetStore.id, range.from, range.to),
          api.feed(targetOrganization.id, targetStore.id, cursorRef.current),
        ]);
        const safeFeed: MerchantFeedPage = feed.resyncRequired
          ? { ...feed, events: [], nextCursor: feed.latestCursor }
          : feed;
        cursorRef.current = safeFeed.nextCursor;
        const next = {
          feed: safeFeed,
          orders,
          savedAt: new Date().toISOString(),
          statement,
          workspace,
        };
        setSnapshot(next);
        await storage.putSnapshot(targetStore.id, next);
        setConnection("live");
      } catch (error) {
        if (cached) setSnapshot(cached);
        setConnection("offline");
        if (!(error instanceof ApiError && error.status === 0)) setMessage(errorMessage(error));
      }
    },
    [api, storage],
  );

  const selectStore = useCallback(
    async (targetOrganization: Organization, targetStore: Store) => {
      setOrganization(targetOrganization);
      setStore(targetStore);
      localStorage.setItem(
        "gobro-merchant-selection",
        JSON.stringify({ organization: targetOrganization, store: targetStore }),
      );
      setPhase("dashboard");
      await refresh(targetOrganization, targetStore);
    },
    [refresh],
  );

  const boot = useCallback(async () => {
    try {
      const me = await api.me();
      setLocale(me.locale);
      const organizationList = itemsFrom(await api.organizations());
      const storeLists = await Promise.all(
        organizationList.map(async (entry) => ({
          organization: entry,
          stores: itemsFrom(await api.stores(entry.id)),
        })),
      );
      setOrganizations(organizationList);
      setStores(storeLists.flatMap((entry) => entry.stores));
      const parsed = readSavedSelection();
      const selected =
        parsed &&
        storeLists.some(
          (entry) =>
            entry.organization.id === parsed.organization.id &&
            entry.stores.some((item) => item.id === parsed.store.id),
        )
          ? parsed
          : null;
      if (selected) await selectStore(selected.organization, selected.store);
      else if (storeLists.length === 1 && storeLists[0]?.stores.length === 1) {
        await selectStore(storeLists[0].organization, storeLists[0].stores[0] as Store);
      } else setPhase("select");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setPhase("login");
      else {
        const parsed = readSavedSelection();
        if (parsed) {
          setOrganization(parsed.organization);
          setStore(parsed.store);
          setSnapshot(await storage.getSnapshot(parsed.store.id));
          setPhase("dashboard");
          setConnection("offline");
        } else {
          setMessage(errorMessage(error));
          setPhase("login");
        }
      }
    }
  }, [api, selectStore, storage]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  useEffect(() => {
    if (!isPreview) void boot();
  }, [boot, isPreview]);

  useEffect(() => {
    void readMutations();
  }, [readMutations]);

  const runSync = useCallback(async () => {
    if (!organization || !store || !navigator.onLine) return;
    setConnection("sync");
    const result = await syncEngine.flush();
    await readMutations();
    if (result.conflicted.length || result.failed.length) setMessage(tr("conflict"));
    await refresh(organization, store);
  }, [organization, readMutations, refresh, store, syncEngine, tr]);

  useEffect(() => {
    const online = () => void runSync();
    const offline = () => setConnection("offline");
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [runSync]);

  const notifyNewOrder = useCallback(
    (eventCursor: string) => {
      if (BigInt(eventCursor) <= BigInt(cursorRef.current)) return;
      cursorRef.current = eventCursor;
      if (audioEnabled && audioRef.current) playAlert(audioRef.current);
      if (
        document.visibilityState === "hidden" &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        new Notification(locale === "ar" ? "طلب جديد على GoBro" : "Nouvelle commande GoBro");
      }
    },
    [audioEnabled, locale],
  );

  useEffect(() => {
    if (isPreview || phase !== "dashboard" || !organization || !store) return;
    const url = `${api.baseUrl}/merchant/organizations/${organization.id}/stores/${store.id}/operations/events/stream?afterCursor=${encodeURIComponent(cursorRef.current)}`;
    const source = new EventSource(url, { withCredentials: true });
    source.onopen = () => setConnection("live");
    source.addEventListener("merchant-order", (raw) => {
      const event = JSON.parse((raw as MessageEvent).data) as { cursor: string; eventType: string };
      if (event.eventType === "order.placed.v1") notifyNewOrder(event.cursor);
      else if (BigInt(event.cursor) > BigInt(cursorRef.current)) cursorRef.current = event.cursor;
      void refresh(organization, store);
    });
    source.addEventListener("merchant-resync", () => void refresh(organization, store));
    source.onerror = () => setConnection(navigator.onLine ? "sync" : "offline");
    const fallback = window.setInterval(() => void refresh(organization, store), 10_000);
    return () => {
      source.close();
      window.clearInterval(fallback);
    };
  }, [api.baseUrl, isPreview, notifyNewOrder, organization, phase, refresh, store]);

  const queue = useCallback(
    async (mutation: Omit<QueuedMutation, "attempts" | "state" | "statusMessage">) => {
      await syncEngine.enqueue(mutation);
      await readMutations();
      if (navigator.onLine) await runSync();
      else setConnection("offline");
    },
    [readMutations, runSync, syncEngine],
  );

  const orderAction = useCallback(
    async (
      order: MerchantOrder,
      command: "accept" | "mark-ready" | "reject" | "start-preparation",
      extra: Record<string, unknown> = {},
    ) => {
      if (!organization || !store) return;
      const id = crypto.randomUUID();
      await queue({
        body: { expectedVersion: order.version, ...extra },
        createdAt: new Date().toISOString(),
        entityKey: `order:${order.id}`,
        headers: { "idempotency-key": `merchant-${id}` },
        id,
        method: "POST",
        path: `/merchant/organizations/${organization.id}/stores/${store.id}/orders/${order.id}/${command}`,
      });
    },
    [organization, queue, store],
  );

  const toggleItem = useCallback(
    async (itemId: string, available: boolean) => {
      if (!organization || !store || !snapshot) return;
      const id = crypto.randomUUID();
      await queue({
        body: {
          expectedVersion: snapshot.workspace.profile.version,
          items: [
            { available, itemId, reason: available ? null : "out_of_stock", soldOutUntil: null },
          ],
        },
        createdAt: new Date().toISOString(),
        entityKey: `item:${itemId}`,
        headers: {},
        id,
        method: "PUT",
        path: `/merchant/organizations/${organization.id}/stores/${store.id}/catalog/availability`,
      });
    },
    [organization, queue, snapshot, store],
  );

  const toggleStore = useCallback(async () => {
    if (!organization || !store || !snapshot) return;
    const id = crypto.randomUUID();
    await queue({
      body: {
        acceptingOrders: !snapshot.workspace.profile.acceptingOrders,
        expectedVersion: snapshot.workspace.profile.version,
      },
      createdAt: new Date().toISOString(),
      entityKey: `store:${store.id}`,
      headers: {},
      id,
      method: "PUT",
      path: `/merchant/organizations/${organization.id}/stores/${store.id}/operations/accepting-orders`,
    });
  }, [organization, queue, snapshot, store]);

  const saveCatalogDraft = useCallback(
    async (payload: CatalogDraftWrite) => {
      if (!organization || !store) return;
      const entityKey = `catalog-draft:${store.id}`;
      const queued = await storage.getMutations();
      await Promise.all(
        queued
          .filter(
            (mutation) =>
              mutation.entityKey === entityKey &&
              mutation.state === "pending" &&
              mutation.path.endsWith("/draft"),
          )
          .map((mutation) => storage.deleteMutation(mutation.id)),
      );
      const id = crypto.randomUUID();
      await queue({
        body: payload,
        createdAt: new Date().toISOString(),
        entityKey,
        headers: { "idempotency-key": `catalog-${id}` },
        id,
        method: "PUT",
        path: `/merchant/organizations/${organization.id}/stores/${store.id}/catalog/draft`,
      });
    },
    [organization, queue, storage, store],
  );

  const publishCatalog = useCallback(
    async (expectedVersion: number) => {
      if (!organization || !store) return;
      const id = crypto.randomUUID();
      await queue({
        body: { expectedVersion },
        createdAt: new Date().toISOString(),
        entityKey: `catalog-draft:${store.id}`,
        headers: { "idempotency-key": `catalog-${id}` },
        id,
        method: "POST",
        path: `/merchant/organizations/${organization.id}/stores/${store.id}/catalog/publish`,
      });
    },
    [organization, queue, store],
  );

  const enableAlerts = async () => {
    const context = audioRef.current ?? new AudioContext();
    await context.resume();
    audioRef.current = context;
    setAudioEnabled(true);
    if ("Notification" in window && Notification.permission === "default")
      void Notification.requestPermission().then((permission) => {
        if (permission === "granted") void subscribeMerchantWebPush(api).catch(() => undefined);
      });
    if ("Notification" in window && Notification.permission === "granted")
      void subscribeMerchantWebPush(api).catch(() => undefined);
    playAlert(context);
  };

  const acknowledge = useCallback(async () => {
    if (!organization || !store || !snapshot) return;
    const cursor = snapshot.feed.nextCursor;
    try {
      await api.checkpoint(organization.id, store.id, {
        lastAcknowledgedCursor: cursor,
        lastSeenCursor: cursor,
      });
      setSnapshot({
        ...snapshot,
        feed: {
          ...snapshot.feed,
          checkpoint: { lastAcknowledgedCursor: cursor, lastSeenCursor: cursor },
        },
      });
    } catch {
      // The durable event cursor remains locally visible and will be acknowledged after reconnect.
    }
  }, [api, organization, snapshot, store]);

  if (phase === "boot") return <LoadingScreen />;
  if (phase === "login" || phase === "otp") {
    return (
      <LoginScreen
        code={code}
        locale={locale}
        message={message}
        phone={phone}
        phase={phase}
        setCode={setCode}
        setLocale={setLocale}
        setPhone={setPhone}
        submit={async () => {
          setMessage(null);
          try {
            if (phase === "login") {
              const challenge = await api.requestOtp(phone);
              setChallengeId(challenge.challengeId);
              setPhase("otp");
            } else if (challengeId) {
              await api.verifyOtp(challengeId, code);
              setPhase("boot");
              await boot();
            }
          } catch (error) {
            setMessage(errorMessage(error));
          }
        }}
      />
    );
  }
  if (phase === "select") {
    return (
      <StoreSelection
        locale={locale}
        organizations={organizations}
        stores={stores}
        selectStore={selectStore}
      />
    );
  }

  return (
    <Dashboard
      acknowledge={acknowledge}
      audioEnabled={audioEnabled}
      connection={connection}
      discardMutation={discardMutation}
      enableAlerts={enableAlerts}
      locale={locale}
      message={message}
      orderAction={orderAction}
      pending={pending}
      publishCatalog={publishCatalog}
      saveCatalogDraft={saveCatalogDraft}
      setLocale={setLocale}
      setMessage={setMessage}
      setTab={(next) => {
        setTab(next);
        if (next === "orders") void acknowledge();
      }}
      snapshot={snapshot}
      store={store}
      tab={tab}
      toggleItem={toggleItem}
      toggleStore={toggleStore}
      tr={tr}
    />
  );
}

function LoadingScreen() {
  return (
    <main className="center-screen">
      <Brand />
      <div className="skeleton wide" />
      <div className="skeleton" />
    </main>
  );
}

function LoginScreen(props: {
  code: string;
  locale: Locale;
  message: string | null;
  phone: string;
  phase: "login" | "otp";
  setCode(value: string): void;
  setLocale(value: Locale): void;
  setPhone(value: string): void;
  submit(): Promise<void>;
}) {
  const tr = (key: Parameters<typeof translate>[1]) => translate(props.locale, key);
  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <Brand />
        <p>PARTENAIRE</p>
        <h1>
          {props.locale === "ar"
            ? "المطبخ واضح حتى في أقوى ساعة ضغط."
            : "Une cuisine lisible, même en plein rush."}
        </h1>
      </section>
      <form
        className="auth-panel"
        onSubmit={(event) => {
          event.preventDefault();
          void props.submit();
        }}
      >
        <button
          className="locale-button"
          type="button"
          onClick={() => props.setLocale(props.locale === "ar" ? "fr" : "ar")}
        >
          {props.locale === "ar" ? "FR" : "عربي"}
        </button>
        <div>
          <span className="eyebrow">GOBRO PRO</span>
          <h2>{tr("login")}</h2>
        </div>
        {props.message && (
          <p className="error-banner" role="alert">
            {props.message}
          </p>
        )}
        <label>
          {props.phase === "login" ? tr("phone") : tr("otp")}
          <input
            dir="ltr"
            inputMode={props.phase === "login" ? "tel" : "numeric"}
            value={props.phase === "login" ? props.phone : props.code}
            onChange={(event) =>
              props.phase === "login"
                ? props.setPhone(event.target.value)
                : props.setCode(event.target.value)
            }
          />
        </label>
        <button className="primary-button" type="submit">
          {tr("next")}
        </button>
        <p className="auth-note">
          {props.locale === "ar"
            ? "الدخول محمي برمز لمرة واحدة، ولا نخزن كلمة مرور."
            : "Connexion protégée par code unique, sans mot de passe stocké."}
        </p>
      </form>
    </main>
  );
}

function StoreSelection({
  locale,
  organizations,
  stores,
  selectStore,
}: {
  locale: Locale;
  organizations: Organization[];
  stores: Store[];
  selectStore(org: Organization, store: Store): Promise<void>;
}) {
  return (
    <main className="select-layout">
      <header>
        <Brand />
        <span>PARTENAIRE</span>
      </header>
      <section>
        <span className="eyebrow">{locale === "ar" ? "مساحة العمل" : "Espace de travail"}</span>
        <h1>{locale === "ar" ? "اختر نقطة البيع" : "Choisissez le point de vente"}</h1>
        <div className="store-list">
          {stores.map((store) => {
            const org =
              organizations.find((entry) => entry.id === store.organizationId) ?? organizations[0];
            if (!org) return null;
            return (
              <button type="button" key={store.id} onClick={() => void selectStore(org, store)}>
                <span className="store-monogram">{store.name.slice(0, 1)}</span>
                <span>
                  <b>{store.name}</b>
                  <small>{org.name}</small>
                </span>
                <Icon name="store" />
              </button>
            );
          })}
        </div>
      </section>
    </main>
  );
}

interface DashboardProps {
  acknowledge(): Promise<void>;
  audioEnabled: boolean;
  connection: Connection;
  discardMutation(id: string): Promise<void>;
  enableAlerts(): Promise<void>;
  locale: Locale;
  message: string | null;
  orderAction(
    order: MerchantOrder,
    command: "accept" | "mark-ready" | "reject" | "start-preparation",
    extra?: Record<string, unknown>,
  ): Promise<void>;
  pending: QueuedMutation[];
  publishCatalog(expectedVersion: number): Promise<void>;
  saveCatalogDraft(payload: CatalogDraftWrite): Promise<void>;
  setLocale(value: Locale): void;
  setMessage(value: string | null): void;
  setTab(value: Tab): void;
  snapshot: DashboardSnapshot | null;
  store: Store | null;
  tab: Tab;
  toggleItem(id: string, available: boolean): Promise<void>;
  toggleStore(): Promise<void>;
  tr(key: Parameters<typeof translate>[1]): string;
}

function Dashboard(props: DashboardProps) {
  const unread = props.snapshot
    ? Number(
        BigInt(props.snapshot.feed.nextCursor) -
          BigInt(props.snapshot.feed.checkpoint.lastAcknowledgedCursor),
      )
    : 0;
  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-brand">
          <Brand />
          <span>PARTENAIRE</span>
        </div>
        <div className="topbar-actions">
          <ConnectionBadge state={props.connection} tr={props.tr} />
          <button
            type="button"
            className="locale-button"
            onClick={() => props.setLocale(props.locale === "ar" ? "fr" : "ar")}
          >
            {props.locale === "ar" ? "FR" : "عربي"}
          </button>
        </div>
      </header>
      <aside className="sidebar">
        <div className="store-identity">
          <span className="store-monogram">{props.store?.name.slice(0, 1)}</span>
          <div>
            <b>{props.store?.name}</b>
            <small>
              {props.snapshot?.workspace.profile.acceptingOrders
                ? props.tr("storeOpen")
                : props.tr("storeClosed")}
            </small>
          </div>
        </div>
        <nav>
          {(["orders", "menu", "finance"] as Tab[]).map((item) => (
            <button
              type="button"
              className={props.tab === item ? "active" : ""}
              key={item}
              onClick={() => props.setTab(item)}
            >
              <Icon name={item === "finance" ? "finance" : item} />
              <span>{props.tr(item)}</span>
              {item === "orders" && unread > 0 && <strong>{unread}</strong>}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span>{props.pending.filter((item) => item.state === "pending").length}</span>
          {props.tr("waitingSync")}
        </div>
      </aside>
      <main className="workspace">
        {props.message && (
          <div className="error-banner inline" role="alert">
            <span>{props.message}</span>
            <button type="button" onClick={() => props.setMessage(null)}>
              ×
            </button>
          </div>
        )}
        {props.connection === "offline" && (
          <div className="offline-banner">
            <Icon name="signal" />
            <span>{props.tr("offlineCopy")}</span>
          </div>
        )}
        {props.pending.some((item) => item.state !== "pending") && (
          <section className="sync-issues" aria-label={props.tr("syncIssue")}>
            {props.pending
              .filter((item) => item.state !== "pending")
              .map((item) => (
                <div key={item.id}>
                  <Icon name="signal" />
                  <span>
                    <b>{props.tr("syncIssue")}</b>
                    <small>{item.statusMessage ?? props.tr("conflict")}</small>
                  </span>
                  <button type="button" onClick={() => void props.discardMutation(item.id)}>
                    {props.tr("discardCommand")}
                  </button>
                </div>
              ))}
          </section>
        )}
        {!props.audioEnabled && props.tab === "orders" && (
          <button
            type="button"
            className="alert-permission"
            onClick={() => void props.enableAlerts()}
          >
            <Icon name="alert" />
            {props.tr("activateAlerts")}
          </button>
        )}
        {!props.snapshot ? (
          <BoardSkeleton />
        ) : props.tab === "orders" ? (
          <OrderBoard {...props} />
        ) : props.tab === "menu" ? (
          <CatalogManager
            key={props.store?.id ?? "catalog"}
            locale={props.locale}
            pending={props.pending}
            publishCatalog={props.publishCatalog}
            saveCatalogDraft={props.saveCatalogDraft}
            snapshot={props.snapshot}
            store={props.store}
            toggleItem={props.toggleItem}
            toggleStore={props.toggleStore}
            tr={props.tr}
          />
        ) : (
          <FinanceView {...props} />
        )}
      </main>
    </div>
  );
}

function ConnectionBadge({ state, tr }: { state: Connection; tr: DashboardProps["tr"] }) {
  return (
    <span className={`connection ${state}`}>
      <i />
      {state === "live"
        ? tr("connectionLive")
        : state === "offline"
          ? tr("connectionOffline")
          : tr("connectionSync")}
    </span>
  );
}

function OrderBoard(props: DashboardProps) {
  const orders = props.snapshot?.orders.items ?? [];
  const lanes = [
    {
      key: "new",
      label: props.tr("newOrders"),
      orders: orders.filter((order) => order.status === "placed"),
    },
    {
      key: "prep",
      label: props.tr("preparing"),
      orders: orders.filter((order) => order.status === "accepted" || order.status === "preparing"),
    },
    {
      key: "ready",
      label: props.tr("ready"),
      orders: orders.filter(
        (order) => order.status === "ready_for_pickup" || order.status === "courier_assigned",
      ),
    },
  ];
  return (
    <>
      <div className="workspace-heading">
        <div>
          <span className="eyebrow">LIVE ORDER FLOW</span>
          <h1>{props.tr("orders")}</h1>
        </div>
        <p>
          {props.locale === "ar"
            ? "الفعل التالي واضح، والسعر موثّق عند القرار."
            : "La prochaine action et chaque montant sont visibles au bon moment."}
        </p>
      </div>
      <div className="order-board">
        {lanes.map((lane) => (
          <section className={`lane lane-${lane.key}`} key={lane.key}>
            <header>
              <span>{lane.label}</span>
              <b>{lane.orders.length}</b>
            </header>
            <div className="lane-list">
              {lane.orders.length ? (
                lane.orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    locale={props.locale}
                    order={order}
                    pending={props.pending.some(
                      (item) => item.entityKey === `order:${order.id}` && item.state === "pending",
                    )}
                    action={props.orderAction}
                    tr={props.tr}
                  />
                ))
              ) : (
                <div className="lane-empty">
                  <Icon name="check" />
                  <span>{props.tr("allCaughtUp")}</span>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function OrderCard({
  locale,
  order,
  pending,
  action,
  tr,
}: {
  locale: Locale;
  order: MerchantOrder;
  pending: boolean;
  action: DashboardProps["orderAction"];
  tr: DashboardProps["tr"];
}) {
  const [prep, setPrep] = useState(20);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState<OrderCancellationReason>("merchant_item_unavailable");
  return (
    <article className={`order-card status-${order.status}`}>
      <div className="order-card-head">
        <div>
          <span className="order-ref">#{order.publicReference}</span>
          <span className="order-time">
            <Icon name="clock" />
            {time(order.createdAt, locale)}
          </span>
        </div>
        {pending && <span className="pending-chip">{tr("waitingSync")}</span>}
      </div>
      <div className="order-lines">
        {order.items.map((line) => (
          <div key={line.id}>
            <b>
              {line.quantity} × {locale === "ar" ? line.itemNameAr : line.itemNameFr}
            </b>
            {line.modifiers.length > 0 && (
              <small>
                {line.modifiers
                  .map((entry) => (locale === "ar" ? entry.optionNameAr : entry.optionNameFr))
                  .join("، ")}
              </small>
            )}
            <span>{money(line.lineTotalMillimes, locale)}</span>
          </div>
        ))}
      </div>
      {order.note && <p className="order-note">{order.note}</p>}
      <div className="receipt">
        <div>
          <span>{locale === "ar" ? "قيمة الأطباق" : "Articles"}</span>
          <b>{money(order.money.subtotalMillimes, locale)}</b>
        </div>
        <div>
          <span>{tr("feeBase")}</span>
          <b>{money(order.money.merchantPlatformBaseFeeMillimes, locale)}</b>
        </div>
        <div className="waiver">
          <span>{tr("feeWaiver")}</span>
          <b>− {money(order.money.merchantPlatformWaiverMillimes, locale)}</b>
        </div>
        <div className="receipt-total">
          <span>{locale === "ar" ? "صافيك من الطلب" : "Votre net"}</span>
          <b>
            {money(
              order.money.subtotalMillimes - order.money.merchantPlatformChargedFeeMillimes,
              locale,
            )}
          </b>
        </div>
      </div>
      {order.status === "placed" && (
        <div className="order-actions">
          <label>
            {tr("prep")}
            <select value={prep} onChange={(event) => setPrep(Number(event.target.value))}>
              <option value={15}>15 min</option>
              <option value={20}>20 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
            </select>
          </label>
          <button
            type="button"
            className="primary-button compact"
            disabled={pending}
            onClick={() => void action(order, "accept", { preparationMinutes: prep })}
          >
            {tr("accept")}
          </button>
          <button
            type="button"
            className="text-button danger"
            disabled={pending}
            onClick={() => setRejecting(!rejecting)}
          >
            {tr("reject")}
          </button>
          {rejecting && (
            <div className="reject-inline">
              <select
                value={reason}
                onChange={(event) => setReason(event.target.value as OrderCancellationReason)}
              >
                <option value="merchant_item_unavailable">
                  {locale === "ar" ? "منتج غير متوفر" : "Article indisponible"}
                </option>
                <option value="merchant_capacity_exceeded">
                  {locale === "ar" ? "طاقة التحضير ممتلئة" : "Capacité dépassée"}
                </option>
                <option value="merchant_closed">
                  {locale === "ar" ? "المطعم مغلق" : "Restaurant fermé"}
                </option>
              </select>
              <button
                type="button"
                className="danger-button"
                onClick={() => void action(order, "reject", { reason })}
              >
                {locale === "ar" ? "تأكيد الرفض" : "Confirmer le refus"}
              </button>
            </div>
          )}
        </div>
      )}
      {order.status === "accepted" && (
        <button
          type="button"
          className="primary-button compact full"
          disabled={pending}
          onClick={() => void action(order, "start-preparation")}
        >
          {tr("startPrep")}
        </button>
      )}
      {order.status === "preparing" && (
        <button
          type="button"
          className="primary-button compact full"
          disabled={pending}
          onClick={() => void action(order, "mark-ready")}
        >
          {tr("ready")}
        </button>
      )}
    </article>
  );
}

function FinanceView(props: DashboardProps) {
  const statement = props.snapshot?.statement;
  return (
    <>
      <div className="workspace-heading">
        <div>
          <span className="eyebrow">TRANSPARENT LEDGER</span>
          <h1>{props.tr("finance")}</h1>
        </div>
        <p>
          {props.locale === "ar"
            ? "كل 300 مليم ظاهرة، سواء أُعفيت أو حُمّلت."
            : "Chaque tranche de 300 millimes est visible, facturée ou exonérée."}
        </p>
      </div>
      {statement && (
        <>
          <section className="balance-band">
            <span>{props.tr("balance")}</span>
            <b>{money(statement.closingBalanceMillimes, props.locale)}</b>
            <small>
              {statement.from} → {statement.to}
            </small>
          </section>
          <div className="statement-table">
            <div className="statement-head">
              <span>{props.locale === "ar" ? "الطلب" : "Commande"}</span>
              <span>{props.locale === "ar" ? "الإجمالي" : "Brut"}</span>
              <span>{props.tr("feeBase")}</span>
              <span>{props.tr("feeWaiver")}</span>
              <span>{props.tr("charged")}</span>
              <span>{props.locale === "ar" ? "الصافي" : "Net"}</span>
            </div>
            {statement.entries.map((entry) => (
              <div className="statement-row" key={entry.journalId}>
                <span>
                  <b>#{entry.publicReference}</b>
                  <small>{time(entry.occurredAt, props.locale)}</small>
                </span>
                <span>{money(entry.grossMillimes ?? 0, props.locale)}</span>
                <span>{money(entry.baseFeeMillimes ?? 0, props.locale)}</span>
                <span className="positive">− {money(entry.waiverMillimes ?? 0, props.locale)}</span>
                <span>{money(entry.chargedFeeMillimes ?? 0, props.locale)}</span>
                <strong>{money(entry.netMillimes ?? 0, props.locale)}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function BoardSkeleton() {
  return (
    <div className="board-skeleton">
      {[1, 2, 3].map((item) => (
        <div key={item}>
          <span className="skeleton wide" />
          <span className="skeleton" />
          <span className="skeleton tall" />
        </div>
      ))}
    </div>
  );
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unexpected error";
}

function readSavedSelection(): { organization: Organization; store: Store } | null {
  const saved = localStorage.getItem("gobro-merchant-selection");
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved) as Partial<{ organization: Organization; store: Store }>;
    return parsed.organization?.id && parsed.store?.id
      ? { organization: parsed.organization, store: parsed.store }
      : null;
  } catch {
    localStorage.removeItem("gobro-merchant-selection");
    return null;
  }
}

function playAlert(context: AudioContext) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(740, context.currentTime);
  oscillator.frequency.setValueAtTime(988, context.currentTime + 0.14);
  gain.gain.setValueAtTime(0.0001, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.38);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.4);
}
